import React from "react";
import PropTypes from "prop-types";
import {
    Empty,
    Spin,
    Button,
    Modal,
    Select,
    message
} from "antd";
import _debounce from "lodash/debounce";
import { AddressRecord } from "records/company-record";
import AddAddressForm from "forms/add-address-form";
import { CompanyService, CaseService } from "services/index";
import SectionBlock from "./components/section-block";
import CardBlock from "./components/card-block";
import CompanyDetailPanel from "./components/company-detail-panel";
import CompanyDocViewerModal from "./components/company-doc-viewer-modal";
import caseService from "services/case-service";
// BrandMixCard now rendered in credit-checker/index.js — kept out of this
// component so Existing Relationship can sit above it. See index.js.
import CompanyScreeningCard from "./company-screening-card";
import {
    canDownloadInitialScreeningReport,
    canViewCompanyVerificationDoc
} from "constants/user-visibility";
import {
    SECTION_IDS,
    COMPANY_DOCUMENT_TILES,
    COMPANY_PAN_TILE,
    PARTNERSHIP_DEED_TILE,
    PARTNERS_LIST_TILE
} from "./constants";


const CREDIT_APPRAISAL_STAGE_ID = 7;

// Groups permitted to edit Reporting Industry / Reporting Anchor — mirrors the
// gate used on case-details-summary.js (the Case Details tab) so both surfaces
// stay in sync.
const REPORTING_EDIT_GROUP_IDS = [4, 5, 7, 18, 20];

// COMPANY_ADDRESS_TYPES.OPERATING / REGISTERED — the ids the address form/API expect.
const ADDRESS_TYPE_OPERATING = 2;
const ADDRESS_TYPE_REGISTERED = 1;

// AddressRecord's own keys. The API address object carries extras (city_name,
// created_date, …) that the Record does not define, so pick only what it and its
// `formValues` getter understand.
const ADDRESS_KEYS = [
    "id",
    "company",
    "associate",
    "address_type",
    "ownership_type",
    "residing_since",
    "address_line_1",
    "address_line_2",
    "city",
    "district",
    "state",
    "country",
    "pincode",
    "is_pv_done",
    "is_communication_address",
    "is_active"
];

function pickAddressFields(raw) {
    const out = {};
    if (!raw) {
        return out;
    }
    ADDRESS_KEYS.forEach(key => {
        if (raw[key] !== undefined) {
            out[key] = raw[key];
        }
    });
    return out;
}

/**
 * Company Detail section — the business-level half of the single-screen
 * underwriting view. Shows company identity (GSTIN, PAN, constitution, vintage,
 * company CIBIL), registered/operating addresses with a "verify with utility
 * bill" affordance, and the company verification documents (GST certificate,
 * utility bill, recommendation letter) in a max-screen viewer.
 */
class CompanyDetailSection extends React.Component {
    state = {
        viewerOpen: false,
        viewerTab: null,
        // Brand-mix state now lives in BrandMixCard (see components/brand-mix-card).
        // Change-ASM modal
        asmModalOpen: false,
        asmSubmitting: false,
        asmSelectedId: null,
        // Operating-address edit modal
        addrModalOpen: false,
        addrSaving: false,
        // Registered-address edit modal
        regAddrModalOpen: false,
        regAddrSaving: false,
        // Reporting Industry / Reporting Anchor edit modal — one modal, two
        // Selects, save both fields in one PATCH.
        reportingModalOpen: false,
        reportingSaving: false,
        reportingIndustryId: null,
        reportingAnchorId: null,
        reportingIndustryOptions: [],
        reportingAnchorOptions: []
    };

    addrFormRef = React.createRef();
    regAddrFormRef = React.createRef();

    openAddressModal = () =>
        this.setState({ addrModalOpen: true, addrSaving: false });

    closeAddressModal = () =>
        this.setState({ addrModalOpen: false, addrSaving: false });

    // Same contract as the applicant Residence/Permanent address edit: PUT the
    // single address through CompanyService.updateSingleAddress, then let the
    // parent re-read the company profile (addresses live on it).
    saveOperatingAddress = () => {
        const { companyId, caseId, company } = this.props;
        const form = this.addrFormRef.current;
        if (!companyId || !form) {
            return;
        }
        const existing =
            (company &&
                company.operatingAddress &&
                company.operatingAddress.record) ||
            null;
        form.validateFields((err, values) => {
            if (err) {
                return;
            }
            this.setState({ addrSaving: true });
            CompanyService.updateSingleAddress(
                companyId,
                new AddressRecord({
                    ...pickAddressFields(values),
                    // Carry the existing row's id so the endpoint updates it
                    // rather than creating a duplicate (null => create).
                    id: (existing && existing.id) || null,
                    company: companyId,
                    // A company address is not tied to an applicant.
                    associate: null,
                    address_type: ADDRESS_TYPE_OPERATING
                }).formValues,
                caseId
            ).then(
                () => {
                    if (this.props.onAddressSaved) {
                        this.props.onAddressSaved();
                    }
                    this.closeAddressModal();
                    message.success("Operating address updated");
                },
                // Second arg (not .catch) so a post-save error is never reported
                // as a failed save.
                () => {
                    this.setState({ addrSaving: false });
                    message.error("Failed to update operating address");
                }
            );
        });
    };

    renderAddressModal() {
        const { company, companyId } = this.props;
        const existing =
            (company &&
                company.operatingAddress &&
                company.operatingAddress.record) ||
            null;
        return (
            <Modal
                visible={this.state.addrModalOpen}
                destroyOnClose
                title={`${existing ? "Edit" : "Add"} Operating Address`}
                okText={"Save"}
                width={800}
                confirmLoading={this.state.addrSaving}
                onOk={this.saveOperatingAddress}
                onCancel={this.closeAddressModal}
            >
                <AddAddressForm
                    ref={this.addrFormRef}
                    values={
                        new AddressRecord({
                            ...pickAddressFields(existing),
                            company: companyId,
                            associate: null,
                            address_type: ADDRESS_TYPE_OPERATING
                        })
                    }
                    addressTypes={[ADDRESS_TYPE_OPERATING]}
                />
            </Modal>
        );
    }

    openRegisteredAddressModal = () =>
        this.setState({ regAddrModalOpen: true, regAddrSaving: false });

    closeRegisteredAddressModal = () =>
        this.setState({ regAddrModalOpen: false, regAddrSaving: false });

    // Same contract as saveOperatingAddress, but for the REGISTERED address.
    saveRegisteredAddress = () => {
        const { companyId, caseId, company } = this.props;
        const form = this.regAddrFormRef.current;
        if (!companyId || !form) {
            return;
        }
        const existing =
            (company &&
                company.registeredAddress &&
                company.registeredAddress.record) ||
            null;
        form.validateFields((err, values) => {
            if (err) {
                return;
            }
            this.setState({ regAddrSaving: true });
            CompanyService.updateSingleAddress(
                companyId,
                new AddressRecord({
                    ...pickAddressFields(values),
                    // Carry the existing row's id so the endpoint updates it
                    // rather than creating a duplicate (null => create).
                    id: (existing && existing.id) || null,
                    company: companyId,
                    // A company address is not tied to an applicant.
                    associate: null,
                    address_type: ADDRESS_TYPE_REGISTERED
                }).formValues,
                caseId
            ).then(
                () => {
                    if (this.props.onAddressSaved) {
                        this.props.onAddressSaved();
                    }
                    this.closeRegisteredAddressModal();
                    message.success("Registered address updated");
                },
                // Second arg (not .catch) so a post-save error is never reported
                // as a failed save.
                () => {
                    this.setState({ regAddrSaving: false });
                    message.error("Failed to update registered address");
                }
            );
        });
    };

    renderRegisteredAddressModal() {
        const { company, companyId } = this.props;
        const existing =
            (company &&
                company.registeredAddress &&
                company.registeredAddress.record) ||
            null;
        return (
            <Modal
                visible={this.state.regAddrModalOpen}
                destroyOnClose
                title={`${existing ? "Edit" : "Add"} Registered Address`}
                okText={"Save"}
                width={800}
                confirmLoading={this.state.regAddrSaving}
                onOk={this.saveRegisteredAddress}
                onCancel={this.closeRegisteredAddressModal}
            >
                <AddAddressForm
                    ref={this.regAddrFormRef}
                    values={
                        new AddressRecord({
                            ...pickAddressFields(existing),
                            company: companyId,
                            associate: null,
                            address_type: ADDRESS_TYPE_REGISTERED
                        })
                    }
                    addressTypes={[ADDRESS_TYPE_REGISTERED]}
                />
            </Modal>
        );
    }

    // Credit Appraisal team (groups 7 + 11) is not allowed to open company
    // documents (GST Certificate, Utility Bill, Recommendation Letter,
    // Takeover Document) — clicking the tile shows a warning instead of
    // opening the viewer. Same rationale as the applicant KYC block.
    // LOA (level_of_authority) L1-L4 users bypass the group block — see
    // canViewCompanyVerificationDoc.
    isDocViewBlocked = () =>
        !canViewCompanyVerificationDoc(this.props.user, this.props.userGroups);

    openViewer = tab => {
        if (this.isDocViewBlocked()) {
            message.warning(
                "You don't have permission to view company documents.",
                3
            );
            return;
        }
        this.setState({ viewerOpen: true, viewerTab: tab || null });
    };

    closeViewer = () => this.setState({ viewerOpen: false });

    // ASM is only re-mappable on Credit Appraisal, and only by the user the case
    // is currently assigned to.
    canEditAsm = () =>
        Number(this.props.stageId) === CREDIT_APPRAISAL_STAGE_ID &&
        Boolean(this.props.isUserAllowedToEdit);

    // Company (Operating/Registered) address edits are only actionable at
    // the credit stages — same gate used elsewhere in Credit Checker (KYC
    // address edits, TVR reset).
    canEditAddress = () => [7, 8].includes(Number(this.props.stageId));

    openAsmModal = () =>
        this.setState({
            asmModalOpen: true,
            asmSelectedId: null,
            asmSubmitting: false
        });

    closeAsmModal = () =>
        this.setState({ asmModalOpen: false, asmSubmitting: false });

    // Re-maps the case's ASM to the picked candidate. The id sent is the one
    // carried by nearest_asm_details.
    handleAsmUpdate = () => {
        const { caseId, onAsmUpdated } = this.props;
        const { asmSelectedId } = this.state;
        if (!caseId || asmSelectedId === null || asmSelectedId === undefined) {
            return;
        }
        this.setState({ asmSubmitting: true });
        caseService
            .updateCaseDetailFields(caseId, {
                sales_manager_user_id: asmSelectedId
            })
            .then(() => {
                message.success("ASM updated");
                this.setState({ asmModalOpen: false, asmSubmitting: false });
                if (onAsmUpdated) {
                    onAsmUpdated();
                }
            })
            .catch(() => {
                message.error("Failed to update ASM");
                this.setState({ asmSubmitting: false });
            });
    };




    // Reporting Industry / Reporting Anchor edit — permission-gated (same
    // group ids the Case Details tab uses), AND additionally restricted to
    // the credit stages (7, 8) — the group check alone let this be edited at
    // any stage, unlike every other edit affordance in this section.
    canEditReporting = () => {
        const groups = this.props.userGroups || [];
        const hasGroup = REPORTING_EDIT_GROUP_IDS.some(id =>
            groups.includes(id)
        );
        return hasGroup && [7, 8].includes(Number(this.props.stageId));
    };

    openReportingModal = () => {
        const company = this.props.company || {};
        this.setState({
            reportingModalOpen: true,
            reportingSaving: false,
            reportingIndustryId: company.reportingIndustryId || null,
            reportingAnchorId: company.reportingAnchorId || null,
            reportingIndustryOptions: [],
            reportingAnchorOptions: []
        });
    };

    closeReportingModal = () =>
        this.setState({ reportingModalOpen: false, reportingSaving: false });

    handleReportingIndustrySearch = search => {
        // Debounce: 300ms via a running timer.
        clearTimeout(this._reportingIndustryTimer);
        this._reportingIndustryTimer = setTimeout(() => {
            CompanyService.industryTypeList(search || "", true)
                .then(res =>
                    this.setState({
                        reportingIndustryOptions: (res && res.data && res.data.results) || []
                    })
                )
                .catch(() => {});
        }, 300);
    };

    // Debounced + light listing, same as the Summary tab's anchor search.
    handleReportingAnchorSearch = _debounce(search => {
        CompanyService.getCompanyProfilesLight(1, search || "", [], true)
            .then(res =>
                this.setState({
                    reportingAnchorOptions: (res && res.data && res.data.results) || []
                })
            )
            .catch(() => {});
    }, 400);

    handleReportingSave = () => {
        const { caseId } = this.props;
        const payload = {};
        if (this.state.reportingIndustryId) {
            payload.reporting_industry = this.state.reportingIndustryId;
        }
        if (this.state.reportingAnchorId) {
            payload.reporting_anchor = this.state.reportingAnchorId;
        }
        if (!Object.keys(payload).length) {
            this.closeReportingModal();
            return;
        }
        this.setState({ reportingSaving: true });
        CaseService.updateCaseDetailV2(caseId, payload)
            .then(() => {
                message.success("Reporting industry / anchor updated");
                this.closeReportingModal();
                // Parent refresh so the header re-renders with the new labels.
                if (this.props.fetchCaseDetail) {
                    this.props.fetchCaseDetail(caseId);
                }
            })
            .catch(() => {
                message.error("Failed to update reporting industry / anchor");
                this.setState({ reportingSaving: false });
            });
    };

    renderReportingModal() {
        const company = this.props.company || {};
        return (
            <Modal
                title={"Edit Reporting Industry / Anchor"}
                visible={this.state.reportingModalOpen}
                onOk={this.handleReportingSave}
                okText={"Save"}
                confirmLoading={this.state.reportingSaving}
                onCancel={this.closeReportingModal}
                destroyOnClose
                width={480}
            >
                <div style={{ marginBottom: 12 }}>
                    <div
                        style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#5f6470",
                            marginBottom: 4
                        }}
                    >
                        Reporting Industry
                    </div>
                    <Select
                        style={{ width: "100%" }}
                        showSearch
                        allowClear
                        placeholder={company.reportingIndustry || "Search industry"}
                        filterOption={false}
                        onSearch={this.handleReportingIndustrySearch}
                        onChange={value =>
                            this.setState({ reportingIndustryId: value || null })
                        }
                        value={this.state.reportingIndustryId || undefined}
                        notFoundContent={"Type to search"}
                    >
                        {(this.state.reportingIndustryOptions || []).map(opt => (
                            <Select.Option key={opt.id} value={opt.id}>
                                {opt.name}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                <div>
                    <div
                        style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#5f6470",
                            marginBottom: 4
                        }}
                    >
                        Reporting Anchor
                    </div>
                    <Select
                        style={{ width: "100%" }}
                        showSearch
                        allowClear
                        placeholder={company.reportingAnchor || "Search anchor"}
                        filterOption={false}
                        onSearch={this.handleReportingAnchorSearch}
                        onChange={value =>
                            this.setState({ reportingAnchorId: value || null })
                        }
                        value={this.state.reportingAnchorId || undefined}
                        notFoundContent={"Type to search"}
                    >
                        {(this.state.reportingAnchorOptions || []).map(opt => (
                            <Select.Option key={opt.id} value={opt.id}>
                                {opt.name}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </Modal>
        );
    }

    // Change-ASM modal: pick one of the pincode-nearest ASMs for this case.
    renderAsmModal() {
        const { company } = this.props;
        const options = (company && company.asmOptions) || [];
        return (
            <Modal
                title={"Change ASM"}
                visible={this.state.asmModalOpen}
                onOk={this.handleAsmUpdate}
                okText={"Update"}
                confirmLoading={this.state.asmSubmitting}
                okButtonProps={{ disabled: this.state.asmSelectedId === null }}
                onCancel={this.closeAsmModal}
                destroyOnClose
                width={420}
            >
                <div
                    style={{
                        fontSize: 12,
                        color: "#5f6470",
                        marginBottom: 4,
                        fontWeight: 600
                    }}
                >
                    Current ASM
                </div>
                <div style={{ marginBottom: 12 }}>{company.asm || "—"}</div>
                <div
                    style={{
                        fontSize: 12,
                        color: "#5f6470",
                        marginBottom: 4,
                        fontWeight: 600
                    }}
                >
                    Nearest ASM
                    <span style={{ color: "#ff4d4f" }}> *</span>
                </div>
                {options.length ? (
                    <Select
                        style={{ width: "100%" }}
                        placeholder={"Select an ASM"}
                        value={
                            this.state.asmSelectedId === null
                                ? undefined
                                : this.state.asmSelectedId
                        }
                        onChange={value =>
                            this.setState({ asmSelectedId: value })
                        }
                    >
                        {options.map(option => (
                            <Select.Option key={option.id} value={option.id}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                ) : (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={"No nearby ASM found for this pincode"}
                    />
                )}
            </Modal>
        );
    }


    render() {
        const { innerRef, company, loading, expanded, onToggle } = this.props;
        // Partnership cases also carry a firm/company PAN and a partnership deed
        // — append those tiles so they show in the verification documents +
        // viewer tabs.
        const docTiles =
            company && ["Partnership" , "Private Limited"].includes(company.constitution)
                ? COMPANY_DOCUMENT_TILES.concat(
                    COMPANY_PAN_TILE,
                    PARTNERSHIP_DEED_TILE,
                    PARTNERS_LIST_TILE
                )
                : COMPANY_DOCUMENT_TILES;
        return (
            <SectionBlock
                id={SECTION_IDS.COMPANY_DETAIL}
                innerRef={innerRef}
                title={"Company Detail"}
                subtitle={
                    "Business identity, addresses and verification documents"
                }
                icon={"bank"}
                expanded={expanded}
                onToggle={onToggle}
            >
                <Spin spinning={Boolean(loading)}>
                    {company ? (
                        <React.Fragment>
                            <CardBlock
                                icon={"bank"}
                                title={company.name || "Company"}
                                subtitle={"Identity, addresses and documents"}
                            >
                                <CompanyDetailPanel
                                    company={company}
                                    tiles={docTiles}
                                    onViewDoc={this.openViewer}
                                    onEditAsm={this.openAsmModal}
                                    editAsmDisabled={!this.canEditAsm()}
                                    editAsmTitle={
                                        this.canEditAsm()
                                            ? "Change ASM"
                                            : "ASM can only be changed on Credit Appraisal, by the user the case is assigned to"
                                    }
                                    onEditOperatingAddress={
                                        this.canEditAddress()
                                            ? this.openAddressModal
                                            : null
                                    }
                                    onEditRegisteredAddress={
                                        this.canEditAddress()
                                            ? this.openRegisteredAddressModal
                                            : null
                                    }
                                    onEditReporting={this.openReportingModal}
                                    canEditReporting={this.canEditReporting()}
                                />
                                {/* Company (entity-level) Initial Screening —
                                    inlined inside the first Company Detail card
                                    (no separate section) so the AML/PEP verdict
                                    sits right next to identity + addresses. */}
                                <CompanyScreeningCard
                                    screening={this.props.companyScreening}
                                    companyName={company && company.name}
                                    docViewBlocked={
                                        !canDownloadInitialScreeningReport(
                                            this.props.user,
                                            this.props.stageId
                                        )
                                    }
                                />
                            </CardBlock>

                            {/* Brand Mix moved OUT of Company Detail into
                                its own render slot in credit-checker/index.js
                                (right after Existing Relationship) so credit
                                sees Existing Relationship ABOVE Brand Mix
                                per 2026-07-22 UI ask. */}
                            {this.renderAsmModal()}
                            {this.renderAddressModal()}
                            {this.renderRegisteredAddressModal()}
                            {this.renderReportingModal()}

                            <CompanyDocViewerModal
                                visible={this.state.viewerOpen}
                                docs={company.docs}
                                tiles={docTiles}
                                companyName={company.name}
                                initialTab={this.state.viewerTab}
                                onClose={this.closeViewer}
                            />
                        </React.Fragment>
                    ) : (
                        !loading && (
                            <Empty description={"Company details not available"} />
                        )
                    )}
                </Spin>
            </SectionBlock>
        );
    }
}

CompanyDetailSection.propTypes = {
    innerRef: PropTypes.object,
    caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    companyId: PropTypes.number,
    onAddressSaved: PropTypes.func,
    company: PropTypes.object,
    companyScreening: PropTypes.object,
    dependence: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    brandMix: PropTypes.array,
    brandMixLoading: PropTypes.bool,
    brandMixCount: PropTypes.number,
    brandMixPage: PropTypes.number,
    brandMixPageSize: PropTypes.number,
    brandMixSearch: PropTypes.string,
    onBrandMixPageChange: PropTypes.func,
    onBrandMixSearch: PropTypes.func,
    onBrandMixAdded: PropTypes.func,
    onAsmUpdated: PropTypes.func,
    stageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isUserAllowedToEdit: PropTypes.bool,
    loading: PropTypes.bool,
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
    userGroups: PropTypes.array,
    // Full user record (LOA label) — Initial Screening report download gate.
    user: PropTypes.object,
    fetchCaseDetail: PropTypes.func
};

export default CompanyDetailSection;
